import { ref, provide, inject, onUnmounted, nextTick } from "vue";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { getMobileTourSteps } from "Admin/js/Mobile/utils/const";
import { useRouter } from "vue-router";
import { useAsyncOperation } from "Admin/js/Composables/useAsyncOperation";
import axios from "axios";
import { MOBILE_ANALYTICS_TYPES } from "Admin/js/Constants/menus";
import MobileAnalyticsTracker from "Admin/js/Utilities/MobileAnalyticsTracker";

const symbol = Symbol("mobileTour");

const defaultDriverConfig = {
    animate: true,
    showProgress: true,
    progressText: "{{current}} of {{total}}",
    overlayOpacity: 0.5,
    stagePadding: 4,
    stageRadius: 4,
    showButtons: ["next", "previous", "close"],
    allowKeyboardControl: true,
    disableActiveInteraction: true,
    popoverClass: "mobile-tour-popover",
    popoverOffset: 10,
    nextBtnText: `<span class="text-fontLight">&nbsp;next</span>
    <span class="material-symbols-outlined icon">chevron_forward</span>`,
    prevBtnText: `<span class="material-symbols-outlined icon">chevron_left</span>`,
    doneBtnText: `<span class="text-fontLight">&nbsp;done</span>
    <span class="material-symbols-outlined tour-popover-next-btn-icon icon">chevron_forward</span>`,
};

function useMobileTourState() {
    const { manageAsyncOperation } = useAsyncOperation();

    const router = useRouter();
    const driverInstance = ref(null);
    const isTourActive = ref(false);
    const currentStepIndex = ref(0);
    const tourSteps = ref([]);
    const lastTourIndex = ref(0);
    const tourCompletedLastStep = ref(false);

    function initializeDriver(customConfig = {}) {
        if (driverInstance.value) {
            return driverInstance.value;
        }

        const config = {
            ...defaultDriverConfig,
            ...customConfig,
            onDestroyStarted: () => {},
            onDestroyed: () => {
                const indexToSave = currentStepIndex.value;
                const total = tourSteps.value.length;
                const finishedLastStep = total > 0 && indexToSave === total - 1;
                updateUserState(indexToSave);
                if (finishedLastStep) {
                    tourCompletedLastStep.value = true;
                }
                isTourActive.value = false;
                currentStepIndex.value = 0;
            },
            onNextClick: (element, step, options) => {
                if (!driverInstance.value) {
                    return;
                }

                if (step.onNextClick) {
                    step.onNextClick(element, step, options);
                    return;
                }

                const isLastStep = driverInstance.value.isLastStep();
                if (isLastStep) {
                    driverInstance.value.destroy();
                } else {
                    driverInstance.value.moveNext();
                }
            },
            onPrevClick: (element, step, options) => {
                if (!driverInstance.value) {
                    return;
                }

                if (step.onPrevClick) {
                    step.onPrevClick(element, step, options);
                    return;
                }
                driverInstance.value.movePrevious();
            },
            onCloseClick: (element, step, options) => {
                if (driverInstance.value) {
                    driverInstance.value.destroy();
                }
            },
            onHighlightStarted: async (element, step, config) => {
                currentStepIndex.value = config?.state?.activeIndex ?? 0;
                if (step.onHighlightStarted) {
                    await step.onHighlightStarted(element, step);
                }
            },
            onHighlighted: (element, step, config) => {
                currentStepIndex.value = config?.state?.activeIndex ?? 0;
                if (step.onHighlighted) {
                    step.onHighlighted(element, step);
                }
                updateUserState(currentStepIndex.value);
                const stepNumber = currentStepIndex.value + 1;
                MobileAnalyticsTracker.track({
                    type: MOBILE_ANALYTICS_TYPES.TOUR_GUIDE_TRACKING_MOBILE,
                    data: { step: stepNumber },
                });
            },
        };

        driverInstance.value = driver(config);
        return driverInstance.value;
    }

    async function setSteps() {
        destroyTour();
        await nextTick();
        initializeDriver();
        tourSteps.value = getMobileTourSteps({
            driverInstance: driverInstance.value,
            router,
        });
        driverInstance.value.setSteps(tourSteps.value);
    }

    async function startTour(startIndex = 0) {
        await setSteps();
        if (tourSteps.value.length === 0) {
            console.warn("No tour steps defined. Use setSteps() first.");
            return;
        }

        const actualStepIndex =
            startIndex >= tourSteps.value.length - 1 ? 0 : startIndex;

        const currentStep = tourSteps.value[actualStepIndex];
        if (currentStep) {
            if (currentStep.routeName) {
                await router.push({ name: currentStep.routeName });
                await nextTick();
            }
            if (currentStep.onLoad) {
                await currentStep.onLoad();
            }
        }

        isTourActive.value = true;
        driverInstance.value.drive(actualStepIndex);
    }

    function destroyTour() {
        if (driverInstance.value) {
            driverInstance.value.destroy();
            driverInstance.value = null;
            isTourActive.value = false;
            currentStepIndex.value = 0;
        }
    }

    function updateUserState(index) {
        return manageAsyncOperation(async () => {
            lastTourIndex.value = index;
            const state = {
                mobile_tour_index: index,
            };
            await axios.patch("/user-states", state);
        });
    }

    onUnmounted(() => {
        if (driverInstance.value) {
            driverInstance.value.destroy();
            driverInstance.value = null;
        }
    });

    return {
        driverInstance,
        isTourActive,
        lastTourIndex,
        initializeDriver,
        setSteps,
        startTour,
        destroyTour,
        currentStepIndex,
        updateUserState,
        tourCompletedLastStep,
    };
}

export function provideMobileTour() {
    const state = useMobileTourState();
    provide(symbol, state);
    return state;
}

export function useMobileTour() {
    return inject(symbol);
}
